import React, { Suspense } from "react";
import { Loader2 } from "lucide-react";

import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { LazyControlBoundary } from "@/main/react/LazyControlBoundary";
import type { MediaLightboxProps } from "@/main/react/media/MediaLightbox";

/**
 * The lazy boundary for the media lightbox (AGENTS.md rule 26 — never a bare `import()`; rule 27 — the
 * viewer must not sit on the startup path).
 *
 * <p>Keep this file TINY. It is imported eagerly by every host that can open the viewer, so anything
 * added here is paid for by every page in the product, including pages with no media at all. The
 * overlay, its Radix dialog and its icons all live behind the dynamic import below and are fetched on
 * the first click, never before.
 *
 * <p>Hosts render `<MediaLightboxLazy …>` only while the viewer is open — `React.lazy` starts the fetch
 * when the element is first rendered, so a page nobody clicks through never requests the chunk.
 */
const MediaLightboxImpl = lazyWithRetry<React.ComponentType<MediaLightboxProps>>(
    () => import("@/main/react/media/MediaLightbox").then((module) => ({ default: module.default })),
    "media-lightbox",
);

/**
 * A scrim while the chunk arrives, rather than `null`. The viewer is opened by a deliberate click and
 * nothing else on the page changes, so with no fallback a slow chunk reads as "the tap did nothing".
 * It paints over no server-rendered content (rule 32) — it is a transient overlay for an interaction
 * the user just started, and it is replaced by the viewer itself.
 */
function MediaLightboxFallback() {
    return (
        <div
            className="fixed inset-0 z-layer-overlay flex items-center justify-center bg-[var(--modal-overlay-strong)]"
            data-playwright-id="media-lightbox-loading"
            role="presentation"
        >
            <Loader2 className="size-6 animate-spin text-foreground" aria-hidden={true} />
        </div>
    );
}

export default function MediaLightboxLazy(props: MediaLightboxProps) {
    // Containment (FI-070, 2026-09-15): a chunk that never arrives costs the viewer, not the island that
    // opened it. `null` drops the scrim, so the page the member was reading is back under them, and the
    // boundary logs and marks the failure. Kept tiny for the reason above: the boundary is already in the
    // eager graph through MainHeader.
    return (
        <LazyControlBoundary controlName="media-lightbox" fallback={null}>
            <Suspense fallback={<MediaLightboxFallback />}>
                <MediaLightboxImpl {...props} />
            </Suspense>
        </LazyControlBoundary>
    );
}
